import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import {
  FulfillmentState,
  FulfillmentStopReason,
  GuaranteeWithPrescriptionsModel,
  GuaranteesV2Service,
  PrescriptionsV2Service,
  isAppointmentPlanning,
} from '@lib/guarantees/data-access';
import { FulfillmentOutcomeResult, fulfillmentLabelKeyFor, openFulfillmentOutcome } from '@lib/guarantees/feature';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { TASK_DATA } from '../../../core/contracts/task-data.contract';
import { Task, TaskStatus, TaskType } from '../../../core/models/task.interface';
import * as TaskActions from '../../../core/store/task.actions';
import { TaskDataTargetComponent } from '../../commons/task-data-target/task-data-target.component';

/** Bookings still waiting on an outcome — the same set the guarantee line menu offers "end fulfilment" for. */
const OPEN_FULFILLMENT_STATES: ReadonlySet<FulfillmentState> = new Set([FulfillmentState.Planned, FulfillmentState.Rescheduled, FulfillmentState.InProgress]);

/** The task's visit as its guarantee line holds it, read off the line's latest booking. */
interface PlannedVisit {
  prescriptionId: string;
  planningId: string;
  subject: string;
  state: FulfillmentState;
  stopReason?: FulfillmentStopReason;
  /** Whether the line's latest booking is the one this task was raised for. */
  isTaskBooking: boolean;
}

@Component({
  selector: 'tasks-check-planned-appointment-task-details',
  standalone: true,
  imports: [MatIcon, TaskDataTargetComponent, TranslatePipe, LoaderComponent],
  templateUrl: './check-planned-appointment-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-6' },
})
export class CheckPlannedAppointmentTaskDetailsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly guaranteesService = inject(GuaranteesV2Service);
  private readonly prescriptionsService = inject(PrescriptionsV2Service);

  constructor() {
    inject(MatIconRegistry).addSvgIcon('doctor', inject(DomSanitizer).bypassSecurityTrustResourceUrl('assets/icons/ic_doctor.svg'));
  }

  protected readonly task = inject<Task<TaskType.CheckPlannedAppointment>>(TASK_DATA);
  protected readonly formatDate = formatDate;
  protected readonly taskStatus = TaskStatus;

  protected readonly visit = signal<PlannedVisit | null>(null);
  protected readonly loading = signal<boolean>(true);
  protected readonly loadError = signal<boolean>(false);
  protected readonly saving = signal<boolean>(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly stateLabelKey = computed(() => {
    const visit = this.visit();
    return visit ? fulfillmentLabelKeyFor(visit.state, visit.stopReason) : null;
  });

  protected readonly canEndFulfillment = computed(() => {
    const visit = this.visit();
    return this.task.status !== TaskStatus.Resolved && !!visit?.isTaskBooking && OPEN_FULFILLMENT_STATES.has(visit.state);
  });

  ngOnInit(): void {
    this.loadVisit();
  }

  protected loadVisit(): void {
    const { memberId, guaranteeId } = this.task.data;
    this.loading.set(true);
    this.loadError.set(false);

    if (!memberId || !guaranteeId) {
      this.loading.set(false);
      return;
    }

    this.guaranteesService
      .getGuaranteeWithPrescriptions(memberId, guaranteeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: entry => {
          this.visit.set(this.findVisit(entry));
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  protected endFulfillment(): void {
    const visit = this.visit();
    if (!visit || !this.canEndFulfillment()) return;

    openFulfillmentOutcome(this.dialog, { titleKey: 'guaranteeV2.action.endFulfillment', subject: visit.subject, offersFulfilled: true }).subscribe(result => {
      if (result) this.writeOutcome(visit, result);
    });
  }

  private writeOutcome(visit: PlannedVisit, result: FulfillmentOutcomeResult): void {
    if (!result.fulfilled && !result.stopReason) return;

    const { memberId } = this.task.data;
    const write$: Observable<void> = result.fulfilled
      ? this.prescriptionsService.setFulfillmentState(memberId, visit.prescriptionId, visit.planningId, { state: FulfillmentState.Fulfilled })
      : this.prescriptionsService.stopFulfillment(memberId, visit.prescriptionId, { reason: result.stopReason });

    this.saving.set(true);
    this.errorMessage.set(null);

    write$.pipe(take(1)).subscribe({
      next: () => this.onOutcomeWritten(result),
      error: err => {
        this.saving.set(false);
        this.errorMessage.set(err?.error?.message || 'guaranteeV2.message.writeError');
      },
    });
  }

  private onOutcomeWritten(result: FulfillmentOutcomeResult): void {
    const labelKey = result.fulfilled ? fulfillmentLabelKeyFor(FulfillmentState.Fulfilled) : fulfillmentLabelKeyFor(FulfillmentState.Stopped, result.stopReason);
    const resolution = this.translate.instant('task.message.appointmentStatusChanged', { status: this.translate.instant(labelKey) });
    this.store.dispatch(TaskActions.resolveTask({ task: this.task, payload: { resolution } }));
    this.router.navigate(['/tasks']);
  }

  private findVisit(entry: GuaranteeWithPrescriptionsModel | null): PlannedVisit | null {
    const { prescriptionId, planningId, appointmentId } = this.task.data;
    const prescription = entry?.prescriptions.find(line => line.id === prescriptionId);
    const latest = prescription?.planning?.[prescription.planning.length - 1];
    if (!prescription || !latest) return null;

    const latestAppointmentId = isAppointmentPlanning(latest) ? latest.appointmentId : undefined;

    return {
      prescriptionId: prescription.id,
      planningId: latest.id,
      subject: prescription.name?.trim() || prescription.code,
      state: latest.fulfillmentState,
      stopReason: latest.stopReason,
      isTaskBooking: planningId ? latest.id === planningId : !!appointmentId && latestAppointmentId === appointmentId,
    };
  }

  protected navigateToMemberProfile(): void {
    this.router.navigate(['/members', this.task.data.memberId]);
  }

  protected navigateToAppointment(): void {
    this.router.navigate(['/members', this.task.data.memberId, 'guarantees'], { queryParams: { prescription: this.task.data.prescriptionId } });
  }
}
